<template>
  <div>
    <!-- Compact row: small thumbnail + title, for secondary videos -->
    <button
      v-if="compact"
      type="button"
      class="w-full flex items-center gap-3 p-1.5 rounded-lg text-left hover:bg-background-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary-blue-100"
      :aria-label="ariaLabel"
      @click="handlePlay"
    >
      <div
        class="relative flex-shrink-0 w-28 rounded-md overflow-hidden aspect-video-16-9"
        :class="gradientClass"
      >
        <img
          v-if="thumbnailSrc"
          :src="thumbnailSrc"
          alt=""
          class="absolute inset-0 w-full h-full object-cover"
          draggable="false"
          @load="handleThumbnailLoad"
          @error="handleThumbnailError"
        >
        <!-- Corner play badge keeps the thumbnail's subject clear -->
        <div class="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-white shadow-sm flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M16.1838 8.72367L7.78824 3.53819C6.78891 2.92095 5.5 3.63981 5.5 4.81439V15.1854C5.5 16.3599 6.7889 17.0787 7.78823 16.4616L16.1838 11.2761C17.1328 10.6899 17.1328 9.30979 16.1838 8.72367Z"
              fill="#06070F"
            />
          </svg>
        </div>
      </div>
      <div class="min-w-0">
        <BaseText
          type="label"
          size="sm"
          class="text-text-loud"
        >
          {{ video.title }}
        </BaseText>
        <BaseText
          v-if="video.duration"
          type="body"
          size="xs"
          class="text-text-subdued mt-0.5"
        >
          {{ video.duration }}
        </BaseText>
      </div>
    </button>

    <!-- Video thumbnail card. Renders a gradient placeholder until a
    thumbnail exists. Play, title and duration sit in the bottom band so the
    thumbnail's subject stays uncovered. -->
    <button
      v-else
      type="button"
      class="video-card relative w-full rounded-xl overflow-hidden flex items-end p-3 text-left focus:outline-none focus:ring-2 focus:ring-primary-blue-100"
      :class="[gradientClass, aspectClass]"
      :aria-label="ariaLabel"
      @click="handlePlay"
    >
      <!-- Real thumbnail (when provided) over the gradient fallback -->
      <template v-if="thumbnailSrc">
        <img
          :src="thumbnailSrc"
          alt=""
          class="absolute inset-0 w-full h-full object-cover"
          draggable="false"
          @load="handleThumbnailLoad"
          @error="handleThumbnailError"
        >
        <div class="thumbnail-scrim absolute inset-0" />
      </template>

      <div class="relative w-full flex items-center gap-2.5">
        <div class="play-badge flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M16.1838 8.72367L7.78824 3.53819C6.78891 2.92095 5.5 3.63981 5.5 4.81439V15.1854C5.5 16.3599 6.7889 17.0787 7.78823 16.4616L16.1838 11.2761C17.1328 10.6899 17.1328 9.30979 16.1838 8.72367Z"
              fill="#06070F"
            />
          </svg>
        </div>
        <BaseText
          type="label"
          size="sm"
          class="video-title flex-grow min-w-0 text-white"
        >
          {{ video.title }}
        </BaseText>
        <div
          v-if="video.duration"
          class="flex-shrink-0 px-1.5 py-0.5 rounded-md bg-neutral-malpha-10"
        >
          <BaseText
            type="label"
            size="xs"
            class="text-white"
          >
            {{ video.duration }}
          </BaseText>
        </div>
      </div>
    </button>

    <!-- YouTube player modal (iframe only mounts once played) -->
    <BaseTutorialModal
      v-if="showYoutubeModal"
      :video-url="embedUrl"
      @close="showYoutubeModal = false"
    />
  </div>
</template>

<script>
import posthog from 'posthog-js'
import { youtubeThumbnailUrl, youtubeEmbedUrl, YOUTUBE_PLACEHOLDER_WIDTH } from '@/utils/setup/setupVideo'

// Literal class strings so Tailwind's purge keeps them
const GRADIENT_CLASSES = {
  blue: 'bg-gradient-to-br from-neutral-800 via-primary-blue-300 to-primary-blue-100',
  green: 'bg-gradient-to-br from-neutral-800 via-primary-green-300 to-primary-green-100',
  yellow: 'bg-gradient-to-br from-neutral-800 via-secondary-yellow-300 to-secondary-yellow-100',
  purple: 'bg-gradient-to-br from-neutral-800 via-primary-purple-300 to-primary-purple-100'
}

export default {
  name: 'SetupVideoSlot',
  props: {
    // { provider: 'youtube', youtubeId, start?, title, duration?, gradient, thumbnail? }
    video: {
      type: Object,
      required: true
    },
    aspectClass: {
      type: String,
      default: 'aspect-video-16-9'
    },
    // Small thumbnail + title row instead of the full card
    compact: {
      type: Boolean,
      default: false
    },
    // Analytics context
    section: {
      type: String,
      default: null
    },
    location: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      showYoutubeModal: false,
      thumbnailFallback: false,
      thumbnailFailed: false
    }
  },
  computed: {
    gradientClass () {
      return GRADIENT_CLASSES[this.video.gradient] || GRADIENT_CLASSES.blue
    },
    thumbnailSrc () {
      if (this.thumbnailFailed) return null
      if (this.video.thumbnail) return this.video.thumbnail
      if (!this.video.youtubeId) return null
      return youtubeThumbnailUrl(this.video.youtubeId, this.thumbnailFallback ? 'hqdefault' : 'maxresdefault')
    },
    ariaLabel () {
      return `Play video: ${this.video.title}`
    },
    embedUrl () {
      return youtubeEmbedUrl(this.video.youtubeId, this.video.start)
    }
  },
  watch: {
    'video.youtubeId' () {
      this.thumbnailFallback = false
      this.thumbnailFailed = false
    }
  },
  methods: {
    handleThumbnailLoad (event) {
      if (this.video.youtubeId && !this.thumbnailFallback && event.target.naturalWidth === YOUTUBE_PLACEHOLDER_WIDTH) {
        this.thumbnailFallback = true
      }
    },
    handleThumbnailError () {
      if (this.video.youtubeId && !this.video.thumbnail && !this.thumbnailFallback) {
        this.thumbnailFallback = true
      } else {
        this.thumbnailFailed = true
      }
    },
    handlePlay () {
      posthog.capture('Setup Video Played', {
        section: this.section,
        youtubeId: this.video.youtubeId,
        video_title: this.video.title,
        location: this.location
      })
      this.showYoutubeModal = true
    }
  }
}
</script>

<style scoped>
.aspect-video-16-9 {
  aspect-ratio: 16 / 9;
}
.aspect-video-16-10 {
  aspect-ratio: 16 / 10;
}
/* Keeps the title legible over any thumbnail */
.thumbnail-scrim {
  background: linear-gradient(180deg, rgba(6, 7, 16, 0) 50%, rgba(6, 7, 16, 0.75) 100%);
}
.video-card {
  transition: transform 200ms ease-out;
}
.video-card:hover {
  transform: scale(1.015);
}
.video-card .play-badge {
  transition: transform 200ms ease-out;
}
.video-card:hover .play-badge {
  transform: scale(1.08);
}
.video-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
@media (prefers-reduced-motion: reduce) {
  .video-card,
  .video-card .play-badge {
    transition: none;
  }
}
</style>
